import React from 'react';
import { motion } from 'framer-motion';
import { FaChartLine } from 'react-icons/fa';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatShortDate, formatMoney } from './formatters';

const RevenueTrendCard = ({ dataPoints, isLoading, delay = 0 }) => {
  const { t } = useLanguage();
  const points = Array.isArray(dataPoints) ? dataPoints : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('dashboard.revenueTrend.title')}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('dashboard.revenueTrend.subtitle')}
          </p>
        </div>
        <div className="p-2 rounded-lg bg-indigo-50">
          <FaChartLine className="h-4 w-4 text-indigo-600" />
        </div>
      </div>

      <div className="h-64">
        {isLoading ? (
          <div className="h-full w-full animate-pulse bg-gray-100 rounded-lg" />
        ) : points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-400">
            {t('dashboard.revenueTrend.empty')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#a855f7" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickFormatter={(v) => formatShortDate(v)}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                width={60}
              />
              <Tooltip
                formatter={(v) => formatMoney(v)}
                labelFormatter={(l) => formatShortDate(l)}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#6366f1"
                strokeWidth={2}
                fill="url(#revGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </motion.div>
  );
};

export default RevenueTrendCard;
